import * as Sentry from "@sentry/react";

// Track if Sentry has been initialized
let sentryInitialized = false;

// Initialize Sentry
export const initSentry = () => {
  // Prevent multiple initializations
  if (sentryInitialized) {
    console.log('⚠️ Sentry already initialized, skipping');
    return;
  }
  
  // Only initialize in production or when SENTRY_DSN is set
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  console.log('🔍 Sentry initialization check:', {
    hasDsn: !!dsn,
    dsnPreview: dsn ? `${dsn.substring(0, 30)}...` : 'NOT SET',
    environment: import.meta.env.MODE
  });
  
  if (!dsn) {
    console.log('❌ Sentry not initialized: VITE_SENTRY_DSN not set');
    return;
  }

  console.log('✅ Initializing Sentry...');
  
  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions in production you can reduce this
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    environment: import.meta.env.MODE,
    // Send default PII (IP addresses, etc.)
    sendDefaultPii: true,
    // Ignore common non-critical errors
    ignoreErrors: [
      // Ignore webkit messageHandlers errors (iOS WebView detection)
      /window\.webkit\.messageHandlers/i,
      // Ignore common browser extension errors
      /chrome-extension/i,
      /moz-extension/i,
      // Ignore ResizeObserver errors (non-critical)
      /ResizeObserver loop/i,
      // Ignore React DOM removeChild errors (race conditions with chart libraries)
      /removeChild/i,
      /Failed to execute 'removeChild'/i,
      // Ignore other non-critical DOM manipulation errors
      /NotFoundError.*removeChild/i,
      // Ignore Facebook In-App Browser errors (Android WebView bridge issues)
      /Java object is gone/i,
      /enableButtonsClickedMetaDataLogging/i,
      // Ignore browser regex parsing errors (often from third-party scripts)
      /Invalid regular expression/i,
      // Ignore cross-origin CSSStyleSheet access errors (preview iframe / extensions)
      /Failed to read a named property 'CSSStyleSheet'/i,
      // Ignore Supabase edge function non-2xx errors (expected business logic)
      /Edge Function returned a non-2xx status code/i,
      // In-app browser (Facebook/Android WebView) JS bridge failures
      /Error invoking callWebView/i,
      /Error invoking postMessage/i,
      /Java exception was raised during method invocation/i,
      /Java bridge method invocation error/i,
      /callWebView/i,
      // Injected third-party scripts (extensions, in-app browsers)
      /flock\.js/i,
      /replaceAll is not a function/i,
    ],
    // Drop events whose stack comes from injected/extension scripts
    denyUrls: [
      /flock\.js/i,
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      // Facebook / in-app browser injected bridge scripts
      /^iabjs:\/\//i,
    ],
    // Additional filtering
    beforeSend(event, hint) {
      // Filter out webkit-related errors
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String((error as any).message);
        if (message.includes('webkit.messageHandlers') || 
            message.includes('removeChild') ||
            message.includes('callWebView') ||
            message.includes('Error invoking postMessage') ||
            message.includes('Java exception was raised') ||
            message.includes('Java bridge') ||
            message.includes('NotFoundError')) {
          return null; // Don't send to Sentry
        }
      }
      // Drop events whose frames come from injected third-party scripts
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (frames.some((f) => /flock\.js|extension:\/\/|^iabjs:\/\//i.test(f.filename || ''))) {
        return null;
      }

      return event;
    },
  });
  
  sentryInitialized = true;
  console.log('✅ Sentry initialized successfully!');
};

// Helper to capture custom errors with context
export const captureError = (
  error: Error | string,
  context?: Record<string, any>,
  level: Sentry.SeverityLevel = "error"
) => {
  if (typeof error === 'string') {
    Sentry.captureMessage(error, {
      level,
      contexts: { custom: context },
    });
  } else {
    Sentry.captureException(error, {
      level,
      contexts: { custom: context },
    });
  }
};

// Helper to set user context
export const setSentryUser = (userId: string, email?: string, username?: string) => {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
};

// Helper to clear user context on logout
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Helper to add breadcrumb for tracking user actions
export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: "info",
  });
};

// Helper to start a span for performance monitoring
export const startSpan = <T>(
  options: { name: string; op: string },
  callback: () => T
): T => {
  return Sentry.startSpan(options, callback);
};

// Export Sentry for direct use if needed
export { Sentry };
